import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isUndefined, omitBy } from 'lodash';
import { organizationTreeSelector } from 'slices/organizationHierarchy.slice';
import type { TOrganizationTreeNode } from '@timeedit/types/lib/types';
import type { TEIFilter } from '@timeedit/ui-components';
import type { TEIOption } from '@timeedit/ui-components';
import type { TSavedFilter } from '@timeedit/ui-components/components/Filter/Filter.type';

type IOptionWithDisabled = TEIOption & { disabled?: boolean };
interface OrganizationMappings {
  options: IOptionWithDisabled[];
  orgIdByVirtualId: Record<string, string>;
  virtualIdByOrgId: Record<string, string>;
}

/**
 * Returns organization tree options for Filter/TreeSelect components,
 * along with bidirectional ID mappings (orgId <--> virtualId).
 */
export function useOrganizationOptions({
  isParentDisabled,
}: { isParentDisabled?: boolean } = {}): OrganizationMappings {
  const organizationTree = useSelector(organizationTreeSelector);

  return useMemo(() => {
    const orgIdByVirtualId: Record<string, string> = {};
    const virtualIdByOrgId: Record<string, string> = {};

    const buildOption = (node: TOrganizationTreeNode): IOptionWithDisabled => {
      orgIdByVirtualId[node.virtualId] = node.id;
      if (!virtualIdByOrgId[node.id]) {
        virtualIdByOrgId[node.id] = node.virtualId;
      }
      return {
        value: node.virtualId,
        label: node.name,
        disabled: isParentDisabled && !!node.children?.length,
        children: node.children?.map(buildOption),
      };
    };

    const options = organizationTree.children?.map(buildOption) ?? [];

    return { options, orgIdByVirtualId, virtualIdByOrgId };
  }, [organizationTree, isParentDisabled]);
}

type FilterBindingArgs = {
  isParentDisabled?: boolean;
  fields: readonly string[];
  settings: TEIFilter;
  onChange: (settings: TEIFilter) => void;
  savedFilters: TSavedFilter[];
  onSaveFilter: (name: string, filter: TEIFilter) => Promise<void>;
};

/**
 * Wraps useOrganizationOptions and converts the given filter `fields` between
 * stable orgIds (used by callers/back-end) and per-load virtualIds (used by
 * the Filter/TreeSelect UI). Spread the returned `filterProps` into <Filter>.
 *
 * `fields` is expected to be a stable reference (declare it at module scope).
 */
export function useOrganizationFilterBinding({
  isParentDisabled,
  fields,
  settings,
  onChange,
  savedFilters,
  onSaveFilter,
}: FilterBindingArgs) {
  const { options, orgIdByVirtualId, virtualIdByOrgId } =
    useOrganizationOptions({ isParentDisabled });

  const convertedSettings = useMemo(
    () => mapFields(settings, fields, virtualIdByOrgId),
    [settings, fields, virtualIdByOrgId]
  );

  const convertedSavedFilters = useMemo(
    () =>
      savedFilters.map((s) => ({
        ...s,
        filter: mapFields(s.filter, fields, virtualIdByOrgId),
      })),
    [savedFilters, fields, virtualIdByOrgId]
  );

  const wrappedOnChange = useCallback(
    (s: TEIFilter) => onChange(mapFields(s, fields, orgIdByVirtualId)),
    [onChange, fields, orgIdByVirtualId]
  );

  const wrappedOnSaveFilter = useCallback(
    (name: string, f: TEIFilter) =>
      onSaveFilter(name, mapFields(f, fields, orgIdByVirtualId)),
    [onSaveFilter, fields, orgIdByVirtualId]
  );

  return {
    options,
    filterProps: {
      settings: convertedSettings,
      onChange: wrappedOnChange,
      savedFilters: convertedSavedFilters,
      onSaveFilter: wrappedOnSaveFilter,
    },
  };
}

function mapFields<F extends { filters: Record<string, unknown> }>(
  filter: F,
  fields: readonly string[],
  map: Record<string, string>
): F {
  const next = { ...filter.filters };
  for (const field of fields) {
    if (next[field] != null) next[field] = map[next[field] as string];
  }
  return { ...filter, filters: omitBy(next, isUndefined) } as F;
}
